import React from 'react';

import { Button } from '$partials/ui';
import { formatForDisplay } from '$partials/filters/individual/price-slider';
import { useAreaUnits } from '$contexts/area-units';
import { useExchangeRate } from '$contexts/exchange-rate';
import IconClose from '$partials/icons/icon-close';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {object} props
 * @param {string} props.label
 * @param {() => void} props.onClick
 * @returns
 */
const FilterTermButton = ({
    label,
    onClick
}) => {
    return (
        <Button
            className='button-secondary-filled type-style-eyebrow h-[24px]'
            onClick={onClick}
            renderIcon={<IconClose />}>
            {label}
        </Button>
    );
};

/**
 * @type {import('$types/components/partials/filters/selected.d.ts').SelectedPropertyListingFilters}
 */
export const SelectedPropertyListingFilters = ({
    amenitiesFilter,
    areasFilter,
    bathroomsFilter,
    bedroomsFilter,
    mapBoundsFilter,
    officeFilter,
    outdoorFilter,
    priceFilter,
    sizeFilter,
    tenuresFilter,
    typesFilter,
    newHomeFilter,
}) => {
    let hasSelectedFilters = false;

    const multiSelectFilters = [
        { filter: amenitiesFilter },
        { filter: areasFilter },
        { filter: tenuresFilter },
        { filter: typesFilter },
    ].map((options, i) => {
        const { filter } = options;
        return filter.value?.map((value, j) => {
            const label = filter.options.find((o) => o.value === value)?.label;
            hasSelectedFilters = true;
            return (
                <FilterTermButton
                    key={`${value}-${i}-${j}`}
                    label={label}
                    onClick={() => filter.toggleValue(value)} />
            );
        });
    });

    const singleSelectFilters = [
        {
            filter: bathroomsFilter,
            label: `${bathroomsFilter.options.find((o) => String(o.value) === String(bathroomsFilter.value))?.label} baths`
        },
        {
            filter: bedroomsFilter,
            label: `${bedroomsFilter.options.find((o) => String(o.value) === String(bedroomsFilter.value))?.label} beds`
        },
    ].map((options, i) => {
        const { filter, label } = options;

        if (filter.value) {
            hasSelectedFilters = true;
            return (
                <FilterTermButton
                    key={`${filter.value}-${i}`}
                    label={label}
                    onClick={() => filter.clear()} />
            );
        }
    });

    const { currency, convertToCurrency } = useExchangeRate();

    const displayMinPrice = formatForDisplay(convertToCurrency({ from: 'GBP',
        to: currency,
        amount: priceFilter.value[0] }), currency);
    const displayMaxPrice = formatForDisplay(convertToCurrency({ from: 'GBP',
        to: currency,
        amount: priceFilter.value[1] }), currency);

    const { unit, convertUnitOfArea } = useAreaUnits() || {};

    const displayMinSize = `${new Intl.NumberFormat('en-GB', { notation: 'compact' }).format(convertUnitOfArea({ amount: sizeFilter.value[0],
        from: 'sqft',
        to: unit }))} ${unit}`;
    const displayMaxSize = `${new Intl.NumberFormat('en-GB', { notation: 'compact' }).format(convertUnitOfArea({ amount: sizeFilter.value[1],
        from: 'sqft',
        to: unit }))} ${unit}`;

    const rangeFilters = [
        {
            filter: priceFilter,
            label: `Price: ${displayMinPrice} — ${displayMaxPrice}`
        },
        {
            filter: sizeFilter,
            label: `Size: ${displayMinSize} — ${displayMaxSize}`
        },
    ].map((options, i) => {
        const { filter, label } = options;

        if (filter.value?.[0] !== filter.range[0] || filter.value?.[1] !== filter.range[1]) {
            hasSelectedFilters = true;
            return (
                <FilterTermButton
                    key={`${label}-${i}`}
                    label={label}
                    onClick={() => filter.clear()} />
            );
        }
    });

    const booleanFilters = [
        { filter: outdoorFilter,
            label: 'Outdoor space' },
        { filter: newHomeFilter,
            label: 'New homes' },
    ].map((options, i) => {
        const { filter, label } = options;
        if (filter.value) {
            hasSelectedFilters = true;
            return (
                <FilterTermButton
                    key={`${label}-${i}`}
                    label={label}
                    onClick={() => filter.clear()} />
            );
        }
    });

    if (mapBoundsFilter?.value || officeFilter?.value) {
        hasSelectedFilters = true;
    }

    if (!hasSelectedFilters) {
        return null;
    }

    return (
        <aside
            aria-label='Selected filters'
            className='layout-container layout-site-outer-padding flex flex-wrap gap-8 pb-24'>
            {/* Generic state filters */}
            {mapBoundsFilter?.value &&
                <FilterTermButton
                    key='map-bounds'
                    label={mapBoundsFilter.value.search || 'Selected map area'}
                    onClick={() => mapBoundsFilter.clear()} />
            }
            {officeFilter?.value &&
                <FilterTermButton
                    key='office'
                    label={`Selected office: ${officeFilter.value}`}
                    onClick={() => officeFilter.clear()} />
            }

            {/* Multi-select filters */}
            {multiSelectFilters}

            {/* Single-select filters */}
            {singleSelectFilters}

            {/* Range filters */}
            {rangeFilters}

            {/* Boolean Filters */}
            {booleanFilters}
        </aside>
    );
};

/* ========================================================================= */
/* Export(s) */
/* ========================================================================= */

export default SelectedPropertyListingFilters;
